"use client";

import { SignUpButton } from "@clerk/nextjs";
import { ArrowRightIcon, CheckIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { MARKETING_LANDING_SECTION_IDS } from "@/lib/marketing/landing-anchors";
import {
  landingEditorLocales,
  landingEditorRows,
  landingHero,
  landingHeroBadgeIcon,
  landingIcpArchetypes,
} from "@/lib/marketing/landing-content";
import { cn, scrollToElementById } from "@/lib/utils";

const STATUS_STYLES: Record<(typeof landingEditorRows)[number]["status"], string> = {
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  draft: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  missing: "border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400",
};

const STATUS_LABEL: Record<(typeof landingEditorRows)[number]["status"], string> = {
  approved: "Approved",
  draft: "Draft",
  missing: "Missing",
};

export const MarketingHeroSection = () => {
  const HeroBadgeIcon = landingHeroBadgeIcon;

  const handleHowItWorksClick = () => {
    scrollToElementById(MARKETING_LANDING_SECTION_IDS.howItWorks);
  };

  return (
    <section
      className="grid min-w-0 scroll-mt-24 items-center gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-12"
      id={MARKETING_LANDING_SECTION_IDS.home}
    >
      <div className="flex min-w-0 flex-col gap-5">
        <Badge className="w-fit gap-1" variant="secondary">
          <HeroBadgeIcon className="size-3.5" />
          {landingHero.badge}
        </Badge>

        <h1 className="text-balance font-semibold text-4xl tracking-tight sm:text-5xl">
          {landingHero.title}
        </h1>

        <p className="max-w-xl text-pretty text-muted-foreground text-sm sm:text-base">
          {landingHero.description}
        </p>

        <p className="max-w-xl rounded-lg border border-border/60 bg-muted/40 p-3 text-foreground/80 text-sm">
          {landingHero.icpLine}
        </p>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <SignUpButton fallbackRedirectUrl="/app">
            <Button className="h-9">
              {landingHero.primaryCtaLabel} <ArrowRightIcon className="size-4" />
            </Button>
          </SignUpButton>
          <Button className="h-9" onClick={handleHowItWorksClick} type="button" variant="outline">
            {landingHero.secondaryCtaLabel}
          </Button>
        </div>

        <ul className="grid gap-2 sm:grid-cols-2">
          {landingHero.bullets.map((text) => (
            <li className="flex items-start gap-2 text-muted-foreground text-sm" key={text}>
              <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckIcon className="size-3.5" />
              </span>
              <span>{text}</span>
            </li>
          ))}
        </ul>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-xs">For:</span>
          {landingIcpArchetypes.map((label) => (
            <span
              className="rounded-full border border-border/60 bg-background/80 px-2.5 py-1 text-muted-foreground text-xs"
              key={label}
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      <div className="relative min-w-0 overflow-hidden rounded-[28px]">
        <div className="pointer-events-none absolute -inset-8 -z-10 rounded-[32px] bg-linear-to-br from-primary/15 via-transparent to-transparent blur-2xl" />
        <Card className="overflow-hidden border-border/60 bg-card/40 shadow-sm backdrop-blur supports-backdrop-filter:bg-card/30">
          <div className="flex items-center justify-between gap-3 border-border/60 border-b px-5 py-3">
            <div className="flex items-center gap-2">
              <span className="size-2.5 rounded-full bg-rose-400/70" />
              <span className="size-2.5 rounded-full bg-amber-400/70" />
              <span className="size-2.5 rounded-full bg-emerald-400/70" />
            </div>
            <p className="truncate font-medium text-muted-foreground text-xs">
              checkout-flow · production
            </p>
            <Badge variant="secondary">5 locales</Badge>
          </div>

          <div className="flex flex-wrap gap-1.5 border-border/60 border-b px-5 py-3">
            {landingEditorLocales.map((locale, index) => (
              <span
                className={cn(
                  "rounded-md border px-2 py-0.5 text-xs",
                  index === 0
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border/60 bg-background/60 text-muted-foreground",
                )}
                key={locale}
              >
                {locale}
              </span>
            ))}
          </div>

          <div className="divide-y divide-border/60">
            {landingEditorRows.map((row) => (
              <div
                className="grid gap-1 px-5 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                key={row.key}
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-muted-foreground text-xs">{row.key}</p>
                  <p
                    className={cn(
                      "mt-0.5 truncate text-sm",
                      row.status === "missing" ? "text-muted-foreground italic" : "text-foreground",
                    )}
                  >
                    {row.value}
                  </p>
                </div>
                <span
                  className={cn(
                    "w-fit rounded-md border px-2 py-0.5 font-medium text-[10px] uppercase tracking-wide",
                    STATUS_STYLES[row.status],
                  )}
                >
                  {STATUS_LABEL[row.status]}
                </span>
              </div>
            ))}
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-3 px-5 py-3 text-muted-foreground text-xs">
            <span>3 / 5 keys ready for production</span>
            <span>last edited 4m ago by Maya</span>
          </div>
        </Card>
      </div>
    </section>
  );
};
